// ============================================================
//  APP — ADMIN DASHBOARD
//  Path: src/App.tsx
//
//  FIX (review pass):
//  - PendingCertificationPage had no route, so the page (and the
//    certification queue) couldn't be opened at all. Added at
//    /pending-certification — same roles as the backend's
//    GET /experts/pending-certification; customer_service gets the
//    page's own View Only mode (certify is super_admin/admin only).
//  - Redirects now REPLACE the history entry. Before, pressing Back
//    after a redirect went to the URL that redirected, which redirected
//    again — the Back button looked broken.
//  - Signing in returns to the page you were sent from (e.g. a link to
//    /experts/abc), instead of always the dashboard home. Only in-app
//    paths are used (see LoginPage.tsx).
// ============================================================

import React from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import DashboardLayout from './layouts/DashboardLayout'
import LoginPage       from './pages/LoginPage'
import DashboardPage   from './pages/DashboardPage'
import TasksPage       from './pages/TasksPage'
import PaymentsPage    from './pages/PaymentsPage'
import TransactionsPage from './pages/TransactionsPage'
import ReportsPage     from './pages/ReportsPage'
import TaxReportPage   from './pages/TaxReportPage'
import TaxRatesPage    from './pages/TaxRatesPage'
import TaskCoveragePage from './pages/TaskCoveragePage'
import BackgroundChecksPage from './pages/BackgroundChecksPage'
import PastDueSubscriptionsPage from './pages/PastDueSubscriptionsPage'
import VehicleVerificationsPage from './pages/VehicleVerificationsPage'
import CategoriesPage from './pages/CategoriesPage'
import CategoryCommunicationPage from './pages/CategoryCommunicationPage'
import UsersPage       from './pages/UsersPage'
import CustomersPage   from './pages/CustomersPage'
import CustomerDetailPage from './pages/CustomerDetailPage'
import ExpertsPage     from './pages/ExpertsPage'
import CompaniesPage   from './pages/CompaniesPage'
import WorkerDirectoryPage from './pages/WorkerDirectoryPage'
import ExpertDetailPage from './pages/ExpertDetailPage'
import AdminsPage      from './pages/AdminsPage'
import SettingsPage    from './pages/SettingsPage'
import VersionControlPage from './pages/VersionControlPage'
import TaskerBreakdownPage from './pages/TaskerBreakdownPage'
import GeographyPage from './pages/GeographyPage'
import AdvertisementsPage from './pages/AdvertisementsPage'
import SpotlightsPage from './pages/SpotlightsPage'
import ScanFailedUploadsPage from './pages/ScanFailedUploadsPage'
import PendingCertificationPage from './pages/PendingCertificationPage'
import InsurancePage from './pages/InsurancePage'
import InsurerPage from './pages/InsurerPage'
import SupportPage from './pages/SupportPage'

// An in-app path to return to after sign-in, from ProtectedRoute's
// redirect state — or null. Never a full URL (no open redirect).
const returnPathFrom = (state: unknown): string | null => {
  const from = (state as { from?: { pathname?: string; search?: string } } | null)?.from
  const path = from?.pathname
  if (!path || !path.startsWith('/') || path.startsWith('//') || path === '/login') return null
  return path + (from?.search || '')
}

const ProtectedRoute: React.FC<{ children: React.ReactNode; allowedRoles: string[] }> = ({ children, allowedRoles }) => {
  const { user, isLoading, isAdmin } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return <div className="min-h-screen bg-surface-primary flex items-center justify-center"><div className="text-gray-400">Loading...</div></div>
  }

  if (!isAdmin) return <Navigate to="/login" replace state={{ from: location }} />

  const hasAccess = user?.roles.some(r => allowedRoles.includes(r))
  if (!hasAccess) {
    if (user?.roles.includes('customer_service')) return <Navigate to="/customers" replace />
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

const AppRoutes: React.FC = () => {
  const { user, isAdmin, isLoading } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return <div className="min-h-screen bg-surface-primary flex items-center justify-center"><div className="text-gray-400">Loading...</div></div>
  }

  const getDefaultRoute = () => {
    if (user?.roles.includes('super_admin') || user?.roles.includes('admin')) return '/'
    if (user?.roles.includes('customer_service')) return '/customers'
    return '/login'
  }

  return (
    <Routes>
      <Route path="/login" element={isAdmin
        ? <Navigate to={returnPathFrom(location.state) || getDefaultRoute()} replace />
        : <LoginPage />} />

      <Route path="/" element={
        <ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><DashboardPage /></ProtectedRoute>} />
        <Route path="tasks" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}><TasksPage /></ProtectedRoute>} />
        <Route path="payments" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><PaymentsPage /></ProtectedRoute>} />
        <Route path="transactions" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><TransactionsPage /></ProtectedRoute>} />
        <Route path="reports" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><ReportsPage /></ProtectedRoute>} />
        <Route path="task-coverage" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><TaskCoveragePage /></ProtectedRoute>} />
        <Route path="tax-rates" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><TaxRatesPage /></ProtectedRoute>} />
        <Route path="tax-report" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><TaxReportPage /></ProtectedRoute>} />
        <Route path="background-checks" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}><BackgroundChecksPage /></ProtectedRoute>} />
        <Route path="past-due-subscriptions" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}><PastDueSubscriptionsPage /></ProtectedRoute>} />
        <Route path="insurer" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}><InsurerPage /></ProtectedRoute>} />
        <Route path="insurance" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}><InsurancePage /></ProtectedRoute>} />
        <Route path="pending-certification" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}><PendingCertificationPage /></ProtectedRoute>} />
        <Route path="vehicle-verifications" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}><VehicleVerificationsPage /></ProtectedRoute>} />
        <Route path="categories" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}><CategoriesPage /></ProtectedRoute>} />
        <Route path="communication" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}><CategoryCommunicationPage /></ProtectedRoute>} />
        <Route path="users" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><UsersPage /></ProtectedRoute>} />
        <Route path="customers" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}><CustomersPage /></ProtectedRoute>} />
        <Route path="customers/:id" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}><CustomerDetailPage /></ProtectedRoute>} />
        <Route path="experts" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}><ExpertsPage /></ProtectedRoute>} />
        <Route path="companies" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}><CompaniesPage /></ProtectedRoute>} />
        <Route path="worker-directory" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}><WorkerDirectoryPage /></ProtectedRoute>} />
        <Route path="experts/:id" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}><ExpertDetailPage /></ProtectedRoute>} />
        <Route path="admins" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminsPage /></ProtectedRoute>} />
        <Route path="settings" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}><SettingsPage /></ProtectedRoute>} />
        <Route path="version-control" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><VersionControlPage /></ProtectedRoute>} />
        <Route path="tasker-breakdown" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}><TaskerBreakdownPage /></ProtectedRoute>} />
        <Route path="geography" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}><GeographyPage /></ProtectedRoute>} />
        <Route path="advertisements" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}><AdvertisementsPage /></ProtectedRoute>} />
        <Route path="spotlights" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><SpotlightsPage /></ProtectedRoute>} />
        {/* Same roles as the backend route (admin.routes.js's
            /upload-tickets/scan-failed, requireRole('super_admin', 'admin'))
            — not customer_service, since re-queuing a stuck scan is an
            operational action, not a view. */}
        {/* Help requests from the apps + one-way messages to users.
            Same roles as the backend's /api/admin/support routes. */}
        <Route path="support" element={<ProtectedRoute allowedRoles={['super_admin', 'admin', 'customer_service']}><SupportPage /></ProtectedRoute>} />
        <Route path="scan-failed-uploads" element={<ProtectedRoute allowedRoles={['super_admin', 'admin']}><ScanFailedUploadsPage /></ProtectedRoute>} />
      </Route>

      <Route path="*" element={<Navigate to={isAdmin ? getDefaultRoute() : '/login'} replace />} />
    </Routes>
  )
}

const App: React.FC = () => (
  <BrowserRouter>
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  </BrowserRouter>
)

export default App